import React from "react";
import { useSnapshot } from "valtio";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import {
    CheckboxIcon,
    CrossIcon,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import Creatable from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import {
    setActions,
    setIsActionsClicked,
    setIsEdited,
    store as locationStore,
    setKey,
} from "./store";
import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CodeQR } from "./CodeQR";
import { ModalConfirmationCopy } from "./ModalConfirmationCopy";
import {
    copyUrl,
    downloadQRCode,
    enableCheckbox,
    getModeOfSales,
    getSite,
    API_LOCATION_MANAGEMENT,
} from "./helpers";
import ErrorToast from "@components/Common/ErrorTost";
import { useSelector } from "react-redux";
import { useFetchGlobal } from "../../../api/hooks";
import { useLocationData } from "./useLocationData";
import { useLocationFormValidation } from "./useLocationFormValidation";
import "./index.css";
import { RootState } from "redux/store";
import { HTTP_STATUS_CONFLICT } from "@constants/index";
import { buildApiUrl } from "@helpers/apiUrl";

type LocationFormType = {
    setIsAddLocationClicked: React.Dispatch<React.SetStateAction<boolean>>;
    isConsult: boolean;
    setIsConsult: React.Dispatch<React.SetStateAction<boolean>>;
    shopId: string;
    qrCodeRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
    editedData: Place;
};
export function LocationForm({
    setIsAddLocationClicked,
    isConsult,
    setIsConsult,
    shopId,
    qrCodeRefs,
    editedData,
}: LocationFormType) {
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(locationStore);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const uuidUser = localStorage.getItem("uuidUser");
    const schema: string = useSelector(
        (state: RootState) => state.global.entityDetails.franchise_schema ?? ""
    );

    const [isOpened, setIsOpened] = React.useState(false);
    const [isCopy, setIsCopy] = React.useState(false);
    const [modeOfSales, setModeOfSales] = React.useState<ModeOfSale[]>([]);
    const [modeOfSale, setModeOfSale] = React.useState<ModeOfSale | null>({
        label: "",
        value: "",
    });
    const [message, setMessage] = React.useState<string>("");
    const [designation, setDesignation] = React.useState<string>("");
    const [qrData, setQrData] = React.useState<string>("");
    const [title, setTitle] = React.useState<string>(t("Add a location"));
    const [canValidate, setCanValidate] = React.useState<boolean>(true);

    const locationApiUrl = React.useMemo(
        () =>
            shopId && userID && franchiseID && uuidUser
                ? buildApiUrl(API_LOCATION_MANAGEMENT, {
                      userId: String(userID),
                      shopId,
                      franchiseId: franchiseID,
                      uuidUser,
                  })
                : null,
        [shopId, userID, franchiseID, uuidUser]
    );

    const { existingDesignationsSet } = useLocationData(
        locationApiUrl,
        fetchGlobal
    );

    const {
        inputDesignation,
        setInputDesignation,
        isNotInteger,
        setIsNotInteger,
        isDesignationExist,
        setIsDesignationExist,
        validateDesignation,
        hasError: hasValidationError,
    } = useLocationFormValidation(existingDesignationsSet, editedData);

    async function handleValidate() {
        const trimmedDesignation = designation.trim();
        if (trimmedDesignation === "") {
            setInputDesignation(true);
            return;
        }
        const { notInteger, duplicate } = validateDesignation(
            trimmedDesignation
        );
        if (notInteger) {
            setIsNotInteger(true);
            return;
        }
        if (duplicate) {
            setIsDesignationExist(true);
            return;
        }
        if (shopId === undefined) {
            ErrorToast(t("Attention ! you must select a store."));
        } else {
            if (editedData.Id !== undefined) {
                if (!canValidate) return;
                editLocation(trimmedDesignation);
            } else if (canValidate) addLocation(trimmedDesignation);
        }
    }

    async function addLocation(trimmedDesignation: string) {
        if (!uuidUser || !schema) return;
        setCanValidate(false);
        toast.dismiss();
        let dataLocation = {
            [t("Designation")]: trimmedDesignation,
            modeOfSale: modeOfSale,
        };
        let url = await getSite(
            t,
            dataLocation,
            setQrData,
            schema,
            franchiseID,
            i18nextLng,
            shopId,
            fetchGlobal
        );
        const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}${API_LOCATION_MANAGEMENT}`;
        let id = uuid();
        let data = [
            {
                userId: userID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                shopId: shopId,
                franchiseId: franchiseID,
                adding: true,
                data: {
                    [id]: {
                        designation: trimmedDesignation,
                        modeOfSale: modeOfSale,
                        shopId: shopId,
                        id: id,
                        qrData: url,
                    },
                },
            },
        ];
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        };
        try {
            const data: ApiResponse = await fetchGlobal(
                apiUrlAdd,
                requestOptions
            );
            mutate(apiUrlAdd, data);
            if (data.error) {
                if (data.status === HTTP_STATUS_CONFLICT) {
                    setIsDesignationExist(true);
                    setCanValidate(true);
                    return;
                }
                throw Error("error");
            }
            setKey(uuid());
            toast.success(
                `${t("The QR code has been successfully generated.")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        } catch (e) {
            setCanValidate(true);
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    async function editLocation(trimmedDesignation: string) {
        if (!schema) return;
        setCanValidate(false);
        toast.dismiss();
        let dataLocation = {
            [t("Designation") as string]: trimmedDesignation,
            modeOfSale: modeOfSale,
        };
        let url = await getSite(
            t,
            dataLocation,
            setQrData,
            schema,
            franchiseID,
            i18nextLng,
            shopId,
            fetchGlobal
        );
        const apiUrlUpdate = `${process.env.REACT_APP_API_V2_URL}${API_LOCATION_MANAGEMENT}`;
        let dataConfig = [
            {
                userId: userID,
                operatorId: operatorID,
                shopId: shopId,
                franchiseId: franchiseID,
                uuidUser: uuidUser,
                data: {
                    [editedData.Id as string]: {
                        id: editedData.Id,
                        designation: trimmedDesignation,
                        modeOfSale: modeOfSale,
                        shopId: shopId,
                        qrData: url,
                    },
                },
            },
        ];
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataConfig),
        };
        try {
            const data: ApiResponse = await fetchGlobal(
                apiUrlUpdate,
                requestOptions
            );
            mutate(apiUrlUpdate, data);
            if (data.error) {
                if (data.status === HTTP_STATUS_CONFLICT) {
                    setIsDesignationExist(true);
                    setCanValidate(true);
                    return;
                }
                throw Error("error");
            }
            setKey(uuid());
            toast.success(
                `${t("The QR code has been successfully generated.")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
            setDesignation("");
            setModeOfSale({ label: "", value: "" });
            setIsAddLocationClicked(false);
            setIsEdited(false);
            setCanValidate(true);
        } catch (e) {
            setCanValidate(true);
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    const setQrRef = (id: string) => (el: HTMLDivElement | null) => {
        qrCodeRefs.current[id] = el;
    };

    React.useEffect(() => {
        if (shopId !== undefined) {
            if (editedData.Id !== undefined) {
                setDesignation((editedData[t("Designation")] as string) ?? "");
                setModeOfSale(editedData.modeOfSale ?? null);
                setTitle(t("Edit a location"));
                setQrData(editedData.qrData ?? "");
            } else {
                setDesignation("");
                setModeOfSale(null);
                setTitle(t("Add a location"));
                setQrData("");
            }
        } else {
            setDesignation("");
            setModeOfSale(null);
            setTitle(t("Add a location"));
            setQrData("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedData, franchiseID, shopId, t]);

    React.useEffect(() => {
        if (shopId !== undefined) {
            getModeOfSales(
                userID,
                franchiseID,
                shopId,
                i18nextLng,
                setModeOfSales,
                fetchGlobal
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopId]);
    return (
        <React.Fragment>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5 className="pt-3">{title}</h5>
                    <CrossIcon
                        className="icon-close-notification__clz mdi_icn_FRcloseForm"
                        height={13}
                        width={13}
                        onClick={() => {
                            setIsConsult(false);
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsEdited(false);
                                    setIsAddLocationClicked(false);
                                    setIsEdited(false);
                                    enableCheckbox();
                                });
                            } else {
                                setIsAddLocationClicked(false);
                                setIsEdited(false);
                                enableCheckbox();
                            }
                        }}
                    />
                </CustomCardHeader>
                <CustomCardBody
                    className={`pl-3 pr-5 `}
                    style={{
                        maxHeight: "470px",
                        overflowY: "auto",
                    }}
                >
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3 "
                        >
                            {t("Designation")}
                        </StyledLabel>
                        <StyledTextInput
                            name="designation"
                            placeholder={t("Write")}
                            type="number"
                            autoComplete="off"
                            min="0"
                            step="1"
                            value={designation}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>
                            ) => {
                                if (
                                    e.key === "e" ||
                                    e.key === "E" ||
                                    e.key === "." ||
                                    e.key === "," ||
                                    e.key === "+" ||
                                    e.key === "-"
                                ) {
                                    e.preventDefault();
                                }
                            }}
                            onWheel={(
                                e: React.WheelEvent<HTMLInputElement>
                            ) => {
                                (e.target as HTMLInputElement).blur();
                            }}
                            onPaste={(
                                e: React.ClipboardEvent<HTMLInputElement>
                            ) => {
                                const pastedData = e.clipboardData.getData(
                                    "text"
                                );
                                if (!/^\d+$/.test(pastedData)) {
                                    e.preventDefault();
                                }
                            }}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                const value = e.target.value;
                                if (value !== "" && !/^\d+$/.test(value)) {
                                    return;
                                }
                                setDesignation(value);
                                setIsEdited(true);
                                setInputDesignation(false);
                                const {
                                    notInteger,
                                    duplicate,
                                } = validateDesignation(value);
                                setIsNotInteger(notInteger);
                                setIsDesignationExist(duplicate);
                            }}
                            className={`gem_inp_designation ${
                                hasValidationError ? "invalid__clz" : ""
                            }`}
                        />
                        {inputDesignation ? (
                            <div
                                role="alert"
                                data-testid="error-designation-empty"
                                className="field-error__clz"
                            >
                                {t("Please enter a designation")}
                            </div>
                        ) : isNotInteger ? (
                            <div
                                role="alert"
                                data-testid="error-not-integer"
                                className="field-error__clz"
                            >
                                {t("Please enter an integer value")}
                            </div>
                        ) : isDesignationExist ? (
                            <div
                                role="alert"
                                data-testid="error-designation-exists"
                                className="field-error__clz"
                            >
                                {t("The location already exists")}
                            </div>
                        ) : null}
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel htmlFor="text" className="mt-3">
                            {t("Mode of sale")}
                        </StyledLabel>
                        <Creatable<ModeOfSale>
                            isClearable
                            autocomplete="off"
                            name="saleMethod"
                            onChange={(e) => {
                                const value = (e as ModeOfSale | null) ?? null;
                                setModeOfSale(value);
                                setIsEdited(true);
                            }}
                            value={modeOfSale}
                            options={modeOfSales}
                            placeholder={`${t("Select")}...`}
                            type="text"
                            maxMenuHeight={150}
                            isValidNewOption={() => false}
                            autoFocus={false}
                        />
                    </AvGroup>
                    <AvGroup className={`${isConsult ? "pe-none__clz" : ""}`}>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("QR code")}
                        </StyledLabel>
                        <CodeQR
                            qrData={editedData.Id !== undefined ? qrData : ""}
                            qrCodeRefs={qrCodeRefs}
                            isModified={
                                editedData.Id !== undefined
                                    ? editedData.modeOfSale?.label !==
                                          modeOfSale?.label ||
                                      editedData[t("Designation")] !==
                                          designation
                                    : false
                            }
                            setIsOpened={setIsOpened}
                            ref={setQrRef(editedData.Id ?? "")}
                            Id={editedData.Id ?? ""}
                            setMessage={setMessage}
                            setIsCopy={setIsCopy}
                        />
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 mdi_btn_FRcancel cmn_btn_cancel"
                        variant="light"
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setIsAddLocationClicked(false);
                                    setIsEdited(false);
                                    enableCheckbox();
                                });
                            } else {
                                setIsAddLocationClicked(false);
                                setIsEdited(false);
                                enableCheckbox();
                            }
                            setIsConsult(false);
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className={`mdi_btn_FRvalidate cmn_btn_validate ${classnames(
                            { "not-allowed-input-vr__clz": isConsult }
                        )}`}
                        variant="primary"
                        onClick={handleValidate}
                    >
                        {t("Generate QR code")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>
            {isOpened ? (
                <ModalConfirmationCopy
                    isModalOpened={isOpened}
                    setIsModalOpened={setIsOpened}
                    validation={() => {
                        isCopy
                            ? copyUrl(qrData, t)
                            : downloadQRCode(
                                  editedData.Id as string,
                                  qrCodeRefs,
                                  t
                              );
                    }}
                    message={message}
                />
            ) : null}
        </React.Fragment>
    );
}
